// Opening a seed in Audiotool, from the browser.
//
// The same push the extension does for "Open in Audiotool" — the core is
// imported, not copied (@audiotool-core → extension/src/audiotool, whose
// arrangement-push/uploads/write/read/transact modules have no Node or Live
// imports). What is browser-specific lives here:
//   • audio bytes come through this app's own R2 proxy (objectUrl);
//   • content tags use Web Crypto (same sha-256 as the extension's node:crypto,
//     so the same bytes get the same tag);
//   • the sample library (what this browser has already uploaded to the
//     account) and the seed → project map persist in localStorage.
//
// Three shapes of seed are handled, decided on the data:
//   per-clip audio (current) · stems with an arrangement (older) · stems with
//   no arrangement at all (oldest — one lane per stem at the seed's bpm).

import type { AudiotoolClient } from "@audiotool/nexus";
import { pushArrangement, seedInputs, stemSeedInputs, type ProgressUpdate, type PushInputs, type PushReport } from "@audiotool-core/arrangement-push.js";
import type { PushReporter } from "@audiotool-core/progress.js";
import { linkQuietly } from "@audiotool-core/link.js";
import { errorText, isSdkError, sdkUploader } from "@audiotool-core/uploads.js";
import { emptyLedger, type SampleLibrary } from "@shared/audiotool/push-reconcile.js";
import { studioUrl } from "@shared/audiotool/link.js";
import type { Snapshot } from "@shared/seed-history.js";
import { objectUrl } from "@web-shared/lib/api";
import type { SeedDetail } from "@web-shared/lib/types";

/** The "Seedbed" app at developer.audiotool.com/applications (public PKCE id). */
export { DEFAULT_CLIENT_ID as AUDIOTOOL_CLIENT_ID } from "@audiotool-core/config.js";
/** What opening a seed needs: create projects, upload samples. `offline` keeps
 * the session alive past the ~1 h access token. */
export const AUDIOTOOL_SCOPE = "user:read project:read project:write sample:read sample:write offline";

// The studio URL a project opens at, and its inverse for a pasted link — shared
// with the extension, which writes the same links.
export { projectNameFromUrl, studioUrl } from "@shared/audiotool/link.js";
/** 20 hex chars of sha-256 — identical to the extension's audioTag. */
export async function webAudioTag(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", Uint8Array.from(bytes));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 20);
}

export async function fetchBytes(url: string): Promise<Uint8Array> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return new Uint8Array(await res.arrayBuffer());
}

/** A seed with no arrangement snapshot: one lane per stem, tempo from the seed. */
export function syntheticSnapshot(detail: SeedDetail): Snapshot {
  return {
    song: {
      tempo: detail.seed.bpm ?? 120,
      gridQuantization: { value: 0, label: "", isTriplet: false },
      scale: { rootNote: 0, rootNoteName: "C", name: "", mode: false, intervals: [] },
      cuePoints: [],
    },
    audioTracks: [...detail.stems]
      .sort((a, b) => a.trackIndex - b.trackIndex)
      .map((s) => ({
        name: s.name, mute: false, solo: false, arm: false, mutedViaSolo: false, volume: 0.85, panning: 0,
        groupTrackName: null, arrangementClips: [], takeLanes: [],
      })),
    totals: { audioTrackCount: detail.stems.length, arrangementClipCount: 0, takeLaneClipCount: 0 },
  };
}

/** Everything to push for a seed, whatever shape it is. */
export async function seedPushInputs(
  detail: SeedDetail,
  fetch: (url: string) => Promise<Uint8Array> = fetchBytes,
  progress: { update?: ProgressUpdate; report?: PushReporter } = {},
  urlOf: (key: string) => string = objectUrl,
): Promise<PushInputs> {
  const arrangement = detail.arrangement;
  const perClip = arrangement?.audioTracks.some((t) => t.arrangementClips.some((c) => Boolean(c.audioKey))) ?? false;
  // Every clip's object — audio clips and MIDI clips' bounces alike.
  const urls: Record<string, string> = {};
  for (const t of [...(arrangement?.audioTracks ?? []), ...(arrangement?.midiTracks ?? [])]) {
    for (const c of t.arrangementClips) {
      if (c.audioKey) urls[c.audioKey] = urlOf(c.audioKey);
      // A browser warp's render: the push reads the clip through it (withRenders).
      if ("render" in c && c.render?.key) urls[c.render.key] = urlOf(c.render.key);
    }
  }
  if (arrangement && perClip) return seedInputs(arrangement, urls, fetch, webAudioTag, progress);
  if (detail.stems.length > 0) {
    const stems = detail.stems.map((s) => ({ name: s.name, trackIndex: s.trackIndex, url: urlOf(s.key) }));
    return stemSeedInputs(arrangement ?? syntheticSnapshot(detail), stems, fetch, webAudioTag, progress);
  }
  if (arrangement?.midiTracks?.length) {
    // MIDI-only seed: the notes, and their bounces when the seed has them.
    return seedInputs(arrangement, urls, fetch, webAudioTag, progress);
  }
  throw new Error("This seed has no audio or MIDI to open.");
}

export interface ExportResult {
  projectName: string;
  displayName: string;
  studioUrl: string;
  report: PushReport;
  /** Why the project couldn't be linked to the seed, or null when it is. */
  linkNote: string | null;
}

/** A new Audiotool project holding the seed. */
export async function exportSeed(
  client: Pick<AudiotoolClient, "projects" | "samples" | "open" | "presets">,
  detail: SeedDetail,
  opts: {
    library: SampleLibrary;
    update?: ProgressUpdate;
    report?: PushReporter;
    fetch?: (url: string) => Promise<Uint8Array>;
    urlOf?: (key: string) => string;
    /** Where the seed link written onto the project points (the web app's origin); defaults to app.seed-bed.com. */
    linkOrigin?: string;
  },
): Promise<ExportResult> {
  const update = opts.update ?? (async () => {});
  const progress = { ...(opts.update ? { update: opts.update } : {}), ...(opts.report ? { report: opts.report } : {}) };
  const inputs = await seedPushInputs(detail, opts.fetch, progress, opts.urlOf);
  opts.report?.({ kind: "stage", stage: "project" });
  await update("Creating the Audiotool project…", 55);
  const made = await client.projects.createProject({ project: { displayName: detail.seed.title } });
  if (isSdkError(made) || !made.project) throw new Error(`Couldn't create the project: ${isSdkError(made) ? errorText(made) : "no project returned"}`);
  const projectName = made.project.name;
  const doc = await client.open(projectName);
  await doc.start();
  try {
    const report = await pushArrangement(inputs, { doc, ledger: emptyLedger(projectName), library: opts.library, upload: sdkUploader(client), presets: client }, progress);
    // Mark the project as this seed's, so the seed's page finds it again — from any browser.
    const seed = detail.seed;
    const linkNote = await linkQuietly(client, projectName, { rootSeedId: seed.rootSeedId ?? seed.id, seedId: seed.id }, opts.linkOrigin);
    opts.report?.({ kind: "stage", stage: "done" });
    await update("Done.", 100);
    return { projectName, displayName: seed.title, studioUrl: studioUrl(projectName), report, linkNote };
  } finally {
    await doc.stop();
  }
}

/** One line about an export, and anything the user should know. */
export function describeExport(r: ExportResult): { summary: string; notes: string[] } {
  const failed = r.report.write.failures.length + r.report.uploads.failed.length;
  const summary =
    `${r.report.uploads.uploaded.length} sample(s) uploaded, ${r.report.ops} change(s) written` +
    (failed > 0 ? `; ${failed} didn't apply` : "") + ".";
  const notes = [
    ...r.report.sourceWarnings,
    ...r.report.planWarnings.map((w) => w.detail),
    ...r.report.write.failures.map((f) => f.error),
    ...r.report.uploads.failed.map((f) => `upload failed: ${f.error}`),
    ...(r.linkNote ? [r.linkNote] : []),
  ];
  return { summary, notes };
}

/**
 * Where a seed link written onto a project should point: this origin in the
 * web app, whose seed pages anyone can open; the default (app.seed-bed.com)
 * from the admin visualizer, whose pages sit behind its token.
 */
export function webLinkOrigin(): { linkOrigin?: string } {
  return import.meta.env.MODE === "account" ? { linkOrigin: window.location.origin } : {};
}

// ---------------------------------------------------------------------------
// Browser persistence
// ---------------------------------------------------------------------------

const LIBRARY_KEY = "seedbed.audiotool.library";
const EXPORTS_KEY = "seedbed.audiotool.exports";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or disabled — the next export just re-uploads */
  }
}

export function loadWebLibrary(): SampleLibrary {
  const lib = readJson<SampleLibrary>(LIBRARY_KEY, { byTag: {} });
  return { byTag: lib.byTag ?? {} };
}

export function saveWebLibrary(lib: SampleLibrary): void {
  writeJson(LIBRARY_KEY, lib);
}

export interface RememberedExport {
  projectName: string;
  studioUrl: string;
  at: string;
}

export function rememberedExport(channel: string, seedId: string): RememberedExport | null {
  return readJson<Record<string, RememberedExport>>(EXPORTS_KEY, {})[`${channel}:${seedId}`] ?? null;
}

export function rememberExport(channel: string, seedId: string, r: ExportResult): void {
  const all = readJson<Record<string, RememberedExport>>(EXPORTS_KEY, {});
  all[`${channel}:${seedId}`] = { projectName: r.projectName, studioUrl: r.studioUrl, at: new Date().toISOString() };
  writeJson(EXPORTS_KEY, all);
}

// ---------------------------------------------------------------------------
// An export interrupted by the sign-in redirect
// ---------------------------------------------------------------------------

const PENDING_KEY = "seedbed.audiotool.pendingExport";

/** Remember that the user asked to export this seed, across the sign-in redirect. */
export function markPendingExport(seedId: string): void {
  try {
    sessionStorage.setItem(PENDING_KEY, seedId);
  } catch {
    /* no storage: the user clicks again after signing in */
  }
}

/** True once, if an export of `seedId` was waiting on sign-in. */
export function takePendingExport(seedId: string): boolean {
  try {
    if (sessionStorage.getItem(PENDING_KEY) !== seedId) return false;
    sessionStorage.removeItem(PENDING_KEY);
    return true;
  } catch {
    return false;
  }
}
